// Module 3.2b — Drill String storage layer.
//
// Storage key: rsc.drillstring.v1
//
// Each record carries `wellId` for ownership. Public list/read helpers
// always scope by wellId.
//
// Auto-sequencing is enforced on every create / update — sections[i].sequence
// is always exactly i after persistence.
//
// Side-effect: every create / update / delete refreshes
// WellProfile.children.drillStrings[] with the lightweight summary so the
// Well Status panel can render count / current string without loading bulk
// payloads.

import {
  emptyDrillStringRecord, validateDrillStringRecord,
  resequenceSections, summariseDrillString,
} from "./schema";
import {
  getWellProfile, updateWellProfile,
} from "@/lib/well/storage";
import { scoped } from "@/lib/accountScope";

export const DRILLSTRING_STORAGE_KEY = "rsc.drillstring.v1";

const _now = () => new Date().toISOString();
const _genId = () => `ds_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const _safeParse = (s, fallback) => { try { return JSON.parse(s) ?? fallback; } catch { return fallback; } };

function _readAll()  { return _safeParse(scoped.getItem(DRILLSTRING_STORAGE_KEY), []); }
function _writeAll(rows) { scoped.setItem(DRILLSTRING_STORAGE_KEY, JSON.stringify(rows)); }

function _bake(rec) { return resequenceSections(rec); }
function _validateOrThrow(rec) {
  const v = validateDrillStringRecord(rec);
  if (!v.ok) {
    const e = new Error("Drill string failed validation.");
    e.validationErrors = v.errors;
    throw e;
  }
}

function _refreshWellSummary(wellId) {
  if (!wellId) return;
  const well = getWellProfile(wellId);
  if (!well) return;
  const all = _readAll().filter((r) => r.wellId === wellId && r.isActive !== false);
  const summary = all.map(summariseDrillString);
  try {
    updateWellProfile(wellId, { children: { ...well.children, drillStrings: summary } });
  } catch { /* best-effort */ }
}

// ---------- Public API ----------------------------------------------------

export function createDrillString(payload) {
  const all = _readAll();
  const base = { ...emptyDrillStringRecord(), ...payload };
  base.id = _genId();
  base.createdAt = _now();
  base.updatedAt = base.createdAt;
  base.isActive = true;
  const baked = _bake(base);
  _validateOrThrow(baked);
  all.push(baked);
  _writeAll(all);
  _refreshWellSummary(baked.wellId);

  // RC-1 — Active Drill String ownership lives on the Well. Symmetric with
  // Active BHA (lib/bha/storage.js#createBha). The first Drill String
  // created for a well becomes active automatically; later creations DO
  // NOT replace it silently — see `setActiveDrillStringForWell`.
  if (baked.wellId) {
    const well = getWellProfile(baked.wellId);
    if (well && !well.activeDrillStringId) {
      try { updateWellProfile(baked.wellId, { activeDrillStringId: baked.id }); }
      catch { /* best-effort */ }
    }
  }

  return baked;
}

/**
 * RC-1 — Explicit "Set Active Drill String" action. Mirrors the Active Mud
 * Program and Active BHA patterns. Promoting a different Drill String NEVER
 * happens silently; callers must use this function (typically in response
 * to a UI confirmation).
 */
export function setActiveDrillStringForWell(wellId, drillStringId) {
  if (!wellId) throw new Error("setActiveDrillStringForWell: wellId required");
  const target = drillStringId
    ? _readAll().find((r) => r.id === drillStringId && r.wellId === wellId)
    : null;
  if (drillStringId && !target) {
    throw new Error(`setActiveDrillStringForWell: DS ${drillStringId} not found on well ${wellId}`);
  }
  return updateWellProfile(wellId, { activeDrillStringId: drillStringId || "" });
}

export function getDrillString(id) {
  return _readAll().find((r) => r.id === id) || null;
}

/** Newest-updated first. Always scoped to a wellId. */
export function listDrillStringsForWell(wellId, { includeInactive = false } = {}) {
  return _readAll()
    .filter((r) => r.wellId === wellId && (includeInactive || r.isActive !== false))
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
}

export function updateDrillString(id, patch) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Drill string ${id} not found.`);
  const merged = {
    ...all[i], ...patch,
    id: all[i].id,
    createdAt: all[i].createdAt,
    wellId: all[i].wellId,                 // wellId is immutable
  };
  merged.updatedAt = _now();
  const baked = _bake(merged);
  _validateOrThrow(baked);
  all[i] = baked;
  _writeAll(all);
  _refreshWellSummary(baked.wellId);
  return baked;
}

export function deleteDrillString(id) {
  const all = _readAll();
  const target = all.find((r) => r.id === id);
  if (!target) return 0;
  const next = all.filter((r) => r.id !== id);
  _writeAll(next);
  _refreshWellSummary(target.wellId);
  return 1;
}

export function _clearAllDrillStrings() { scoped.removeItem(DRILLSTRING_STORAGE_KEY); }
